import { useDatasourceId, useQueryId, useSeriesId } from '@/hooks';
import { useGetQueryRowsQuery } from '@/features/query-builder/queriesApiSlice';
import { useGetSavedQueryCachedRowsQuery } from '@/features/query-builder/savedQueriesApiSlice';
import { useAppSelector } from '@/store/hooks';
import { selectFullResultsSeriesId } from '@/features/query-builder/queryBuilderSlice';
import React from 'react';
import { useGetFullQueryResultsQuery } from '@/features/query-builder/fullQueryResultsApiSlice';
import { UUID } from '@/types';
import { useLoadColumns } from './useLoadColumns';

export const useLoadFullResultsRows = (fullResultsSeriesId: Maybe<UUID>) => {
  const datasourceId = useDatasourceId();
  const resultsRef = React.useRef<any>({});

  const [pollingInterval, setPollingInterval] = React.useState(200);

  const skip = !fullResultsSeriesId;

  const result = useGetFullQueryResultsQuery(
    { datasourceId, seriesId: fullResultsSeriesId },
    { pollingInterval, skip },
  );

  React.useEffect(() => {
    if (result.isFetching) {
      setPollingInterval((v) => Math.min(v * 2, 10000));
      resultsRef.current = { isFetching: true, data: null };
    }
  }, [result.isFetching]);

  React.useEffect(() => {
    if (result.data?.ready) {
      setPollingInterval(0);
      resultsRef.current = {
        isFetching: false,
        isSuccess: true,
        data: { ...result.data },
      };
    }
  }, [result.data]);

  return resultsRef.current;
};
/**
 * Encapsulates loading rows for:
 *  1. Ad-hoc queries
 *  2. Saved queries
 *  3. Full results
 */
export const useLoadRows = (skip = false) => {
  const datasourceId = useDatasourceId();
  const queryId = useQueryId();
  const seriesId = useSeriesId();
  const fullResultsSeriesId = useAppSelector(selectFullResultsSeriesId);
  const fullResultsRows = useLoadFullResultsRows(fullResultsSeriesId);

  const { isSuccess: columnsLoaded } = useLoadColumns();

  const queryResult = useGetQueryRowsQuery(
    {
      datasourceId,
      queryId,
    },
    { skip: !(queryId && columnsLoaded) || skip },
  );

  const savedQueryResult = useGetSavedQueryCachedRowsQuery(
    {
      datasourceId,
      seriesId,
    },
    { skip: !(seriesId && columnsLoaded) || skip },
  );

  if (fullResultsSeriesId) return fullResultsRows;

  if (seriesId) return savedQueryResult;

  return queryResult;
};
